import { PropTypes, React } from 'imports';
import TextInput from '../TextInput/TextInput';
import Tooltip from '../Tooltip/Tooltip';
import MarkdownBlock from '../MarkdownBlock/MarkdownBlock';
import { WysiwygEditor } from 'typed/components/common';
import { FormattedResource } from '..';
import { isAncestorOrSelf } from 'utils';
import './EditableLabel.less';

export default class EditableLabel extends React.PureComponent {
    static get propTypes() {
        return {
            value: PropTypes.string,
            placeholder: FormattedResource.resourcePropType,
            labelPlaceholder: FormattedResource.resourcePropType,
            inputPlaceholder: FormattedResource.resourcePropType,
            disabled: PropTypes.bool,
            clearable: PropTypes.bool,
            autofocus: PropTypes.bool,
            className: PropTypes.string,
            multiline: PropTypes.bool,
            formatMarkdown: PropTypes.bool,
            markdownHint: PropTypes.bool,
            markdownWysiwyg: PropTypes.bool,
            maxLength: PropTypes.number,
            onCancel: PropTypes.func,
            onChange: PropTypes.func,
            onPasteImage: PropTypes.func
        };
    }

    static get defaultProps() {
        return {
            value: '',
            placeholder: '',
            labelPlaceholder: '',
            inputPlaceholder: '',
            disabled: false,
            clearable: true,
            autofocus: false,
            className: '',
            multiline: false,
            formatMarkdown: false,
            markdownHint: false,
            markdownWysiwyg: false
        };
    }

    state = { editingValue: null };

    componentWillMount() {
        this.componentWillReceiveProps(this.props);

        if (this.props.autofocus && !this.props.disabled) {
            this.setState({ editingValue: this.props.value || '' });
        }
    }

    componentWillReceiveProps(nextProps) {
        if (this.props.value !== nextProps.value) {
            this.setState({ editingValue: null });
        }
    }

    shouldComponentUpdate(nextProps, nextState) {
        return (
            this.props.value !== nextProps.value ||
            !FormattedResource.equal(this.props.placeholder, nextProps.placeholder) ||
            !FormattedResource.equal(this.props.labelPlaceholder, nextProps.labelPlaceholder) ||
            !FormattedResource.equal(this.props.inputPlaceholder, nextProps.inputPlaceholder) ||
            this.props.disabled !== nextProps.disabled ||
            this.props.className !== nextProps.className ||
            this.props.multiline !== nextProps.multiline ||
            this.props.formatMarkdown !== nextProps.formatMarkdown ||
            this.state.editingValue !== nextState.editingValue ||
            this.state.markdownWysiwyg !== nextState.markdownWysiwyg
        );
    }

    _onClick = (event) => {
        if (this.props.disabled) {
            return;
        }

        if (this.props.formatMarkdown && event.target.tagName === 'A') {
            event.stopPropagation();
        }
        else {
            this.setState({ editingValue: this.props.value || '' });
        }
    };

    _onChange = (sender, value) => {
        this.setState({ editingValue: value });
    };

    _onSubmit = (sender, value) => {
        if (this.props.value !== value) {
            if (this.props.onChange) {
                this.props.onChange(this, value);
            }
        }
        else if (this.props.onCancel) {
            this.props.onCancel(this);
        }


        this.setState({ editingValue: null });
    };

    _onBlur = (sender, value, event) => {
        if (isAncestorOrSelf(this.refs.root, event.relatedTarget)) {
            return;
        }

        this._onSubmitClick();
    };

    _preventEventPropagation(event) {
        if (event) {
            if (Object.prototype.hasOwnProperty.call(event, 'stopPropagation')) { event.stopPropagation(); }
            if (Object.prototype.hasOwnProperty.call(event, 'preventDefault')) { event.preventDefault(); }
        }
    }

    _onSubmitClick = (event) => {
        this._onSubmit(this, this.state.editingValue);
        this._preventEventPropagation(event);
    };

    _onCancelClick = (event) => {
        if (this.props.onCancel) {
            this.props.onCancel(this);
        }

        this.setState({ editingValue: null });
        this._preventEventPropagation(event);
    };

    render() {
        return (
            <div
                ref="root"
                className={
                    'editable-label ' +
                    (this.props.className || '') +
                    (this.props.disabled ? ' disabled' : '') +
                    (this.state.editingValue !== null ? ' editing' : '') +
                    (this.props.markdownWysiwyg ? ' with-wysiwyg-editor' : '')
                }
            >
                {
                    this.state.editingValue === null ?
                        <div className={`label ${this.props.multiline ? 'multiline' : 'singleline'}`}
                            tabIndex={!this.props.disabled ? 0 : null}
                            onClick={!this.props.disabled ? this._onClick : null}
                            onFocus={!this.props.disabled ? this._onClick : null}>
                            {
                                this.props.value ?
                                    this.props.formatMarkdown ? <MarkdownBlock value={this.props.value} /> : this.props.value :
                                    <FormattedResource resource={this.props.labelPlaceholder || this.props.placeholder} />
                            }
                        </div> :
                        null
                }
                {
                    this.state.editingValue !== null && !this.props.markdownWysiwyg ?
                        <TextInput ref="text-input"
                            className={this.props.formatMarkdown ? 'with-hint' : ''}
                            debounce={0}
                            autofocus
                            value={this.state.editingValue}
                            placeholder={this.props.inputPlaceholder || this.props.placeholder}
                            disabled={this.props.disabled}
                            clearable={this.props.clearable}
                            multiline={this.props.multiline}
                            markdownHint={this.props.markdownHint}
                            markdownWysiwyg={this.props.markdownWysiwyg}
                            maxLength={this.props.maxLength}
                            onChange={this._onChange}
                            onPasteImage={this.props.onPasteImage}
                            onSubmit={this._onSubmit}
                            onCancel={this._onCancelClick}
                            onBlur={this._onBlur}
                        /> :
                        null
                }
                {
                    this.state.editingValue !== null && this.props.markdownWysiwyg ?
                        <WysiwygEditor
                            value={this.state.editingValue}
                            autofocus
                            onChange={this._onChange}
                            onBlur={this._onBlur}
                            onPasteImage={this.props.onPasteImage}
                        /> :
                        null
                }
                {
                    this.state.editingValue !== null ?
                        <div className="buttons control-group">
                            <button tabIndex="-1" className="fa fa-check" onClick={this._onSubmitClick}>
                                <Tooltip><FormattedResource resource={{ id: 'editable_label.submit_hint' }} /></Tooltip>
                            </button>
                            <button tabIndex="-1" className="fa fa-undo" onClick={this._onCancelClick}>
                                <Tooltip><FormattedResource resource={{ id: 'editable_label.cancel_hint' }} /></Tooltip>
                            </button>
                        </div> :
                        null
                }
            </div>
        );
    }
}
